/**
 * AuditLogViewer Component
 *
 * Displays configuration change history for entities (User Story 4 - 007-transferhub-refinements)
 * Shows who changed what and when with before/after values.
 */

import React, { useState } from 'react';
import { formatDateTime as formatDateTimeShared, formatRelativeTime as formatRelativeTimeShared, activeTimezone } from '../../utils/formatDateTime';
import { auditLogApi } from '../../services/api/auditLog';
import type {
  AuditLogEntry,
  AuditLogSearchParams,
  AuditEntityType,
  AuditAction,
  AuditHistorySummary,
} from '../../types/auditLog';
import { Button } from '../shared/Button';
import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import { queryKeys } from '../../lib/queryKeys';
import { useTimezone } from '../../contexts/TimezoneContext';

// Stable empty reference: `?? []` would be a new array every render, which turns any downstream
// memo dependency into a guaranteed miss.
const NO_LOGS: AuditLogEntry[] = [];

// THUB-272 / THUB-186: delegate to the single shared, timezone-aware formatter.
// These replace local copies that parsed the backend's (formerly naive) timestamps as
// LOCAL time -- which future-dated every value and made every recent row render
// "Just now". Module-level shims rather than the useTimezone() hook because these are
// called from several sub-components and inside .map callbacks in this file.
function formatDate(value: string | null | undefined): string {
  return formatDateTimeShared(value, activeTimezone(), 'precise');
}
function formatRelativeTime(value: string | null | undefined): string {
  return formatRelativeTimeShared(value, activeTimezone());
}

// Props for entity-specific audit history view
interface EntityAuditViewerProps {
  /** Type of entity to show audit history for */
  entityType: AuditEntityType;
  /** ID of the entity */
  entityId: string;
  /** Optional title override */
  title?: string;
}

// Props for global audit log search view
interface GlobalAuditViewerProps {
  /** Initial search filters */
  initialFilters?: AuditLogSearchParams;
}

type AuditLogViewerProps = EntityAuditViewerProps | GlobalAuditViewerProps;

// Type guard for entity-specific view
function isEntityView(props: AuditLogViewerProps): props is EntityAuditViewerProps {
  return 'entityType' in props && 'entityId' in props;
}

/**
 * Format a date for display
 */
/**
 * Format relative time (e.g., "2 hours ago")
 */
/**
 * Get action badge styling
 */
function getActionBadgeClass(action: AuditAction): string {
  const base = 'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium';
  switch (action) {
    case 'create':
      return `${base} bg-success-100 text-success-700 dark:bg-success-900/30 dark:text-success-400`;
    case 'update':
      return `${base} bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400`;
    case 'delete':
      return `${base} bg-danger-100 text-danger-700 dark:bg-danger-900/30 dark:text-danger-400`;
    default:
      return `${base} bg-neutral-100 text-neutral-700 dark:bg-neutral-700 dark:text-neutral-300`;
  }
}

/**
 * Get action icon
 */
function getActionIcon(action: AuditAction): string {
  switch (action) {
    case 'create':
      return '+';
    case 'update':
      return '~';
    case 'delete':
      return '-';
    default:
      return '?';
  }
}

/**
 * Change diff viewer - shows before/after values
 */
const ChangeDiffViewer: React.FC<{
  previousValue?: Record<string, unknown>;
  newValue?: Record<string, unknown>;
  changeDetails?: string[];
  action: AuditAction;
}> = ({ previousValue, newValue, changeDetails, action }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // For create/delete, show the full object
  // For update, show only changed fields
  const hasChanges = changeDetails && changeDetails.length > 0;

  if (action === 'create' && newValue) {
    return (
      <div className="mt-2">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="text-xs text-primary-600 dark:text-primary-400 hover:underline flex items-center gap-1"
        >
          {isExpanded ? 'Hide' : 'Show'} created values
          <span className={`transform transition-transform ${isExpanded ? 'rotate-90' : ''}`}>
            &gt;
          </span>
        </button>
        {isExpanded && (
          <pre className="mt-2 p-2 bg-success-50 dark:bg-success-900/20 rounded text-xs font-mono overflow-x-auto text-success-700 dark:text-success-300">
            {JSON.stringify(newValue, null, 2)}
          </pre>
        )}
      </div>
    );
  }

  if (action === 'delete' && previousValue) {
    return (
      <div className="mt-2">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="text-xs text-primary-600 dark:text-primary-400 hover:underline flex items-center gap-1"
        >
          {isExpanded ? 'Hide' : 'Show'} deleted values
          <span className={`transform transition-transform ${isExpanded ? 'rotate-90' : ''}`}>
            &gt;
          </span>
        </button>
        {isExpanded && (
          <pre className="mt-2 p-2 bg-danger-50 dark:bg-danger-900/20 rounded text-xs font-mono overflow-x-auto text-danger-700 dark:text-danger-300">
            {JSON.stringify(previousValue, null, 2)}
          </pre>
        )}
      </div>
    );
  }

  if (action === 'update' && hasChanges) {
    return (
      <div className="mt-2">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="text-xs text-primary-600 dark:text-primary-400 hover:underline flex items-center gap-1"
        >
          {isExpanded ? 'Hide' : 'Show'} {changeDetails.length} changed field{changeDetails.length !== 1 ? 's' : ''}
          <span className={`transform transition-transform ${isExpanded ? 'rotate-90' : ''}`}>
            &gt;
          </span>
        </button>
        {isExpanded && (
          <div className="mt-2 space-y-2">
            {changeDetails.map((field) => (
              <div key={field} className="p-2 bg-neutral-50 dark:bg-neutral-800 rounded text-xs">
                <div className="font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                  {field}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <span className="text-neutral-500 dark:text-neutral-400">Before:</span>
                    <pre className="mt-1 p-1.5 bg-danger-50 dark:bg-danger-900/20 rounded font-mono text-danger-700 dark:text-danger-300 overflow-x-auto">
                      {JSON.stringify(previousValue?.[field] ?? 'N/A', null, 2)}
                    </pre>
                  </div>
                  <div>
                    <span className="text-neutral-500 dark:text-neutral-400">After:</span>
                    <pre className="mt-1 p-1.5 bg-success-50 dark:bg-success-900/20 rounded font-mono text-success-700 dark:text-success-300 overflow-x-auto">
                      {JSON.stringify(newValue?.[field] ?? 'N/A', null, 2)}
                    </pre>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  return null;
};

/**
 * Single audit log entry row
 */
const AuditLogRow: React.FC<{
  entry: AuditLogEntry;
  showEntityInfo?: boolean;
}> = ({ entry, showEntityInfo = false }) => {
  // THUB-186: subscribe to the timezone context so this component RE-RENDERS when the
  // selector changes. The formatter shims above read a module cache, which returns the
  // right value but cannot itself trigger a render -- without this the timestamps kept
  // showing the previous zone until a full page reload.
  useTimezone();
  return (
    <div className="p-4 border-b border-neutral-200 dark:border-neutral-700 hover:bg-neutral-50 dark:hover:bg-neutral-800/50 transition-colors">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          {/* Action and entity info */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className={getActionBadgeClass(entry.action)}>
              <span className="mr-1">{getActionIcon(entry.action)}</span>
              {entry.action}
            </span>

            {showEntityInfo && (
              <span className="text-sm text-neutral-600 dark:text-neutral-400">
                <span className="font-medium capitalize">{entry.entityType}</span>
                <span className="mx-1 text-neutral-400 dark:text-neutral-500">&middot;</span>
                <span className="font-mono text-xs">{entry.entityId.slice(0, 8)}...</span>
              </span>
            )}
          </div>

          {/* User info */}
          <div className="mt-1 text-sm text-neutral-600 dark:text-neutral-400">
            <span className="font-medium text-neutral-700 dark:text-neutral-300">
              {entry.userId || 'System'}
            </span>
            {' '}made this change
          </div>

          {/* Change details */}
          <ChangeDiffViewer
            previousValue={entry.previousValue}
            newValue={entry.newValue}
            changeDetails={entry.changeDetails}
            action={entry.action}
          />
        </div>

        {/* Timestamp */}
        <div className="text-right flex-shrink-0">
          <div className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
            {formatRelativeTime(entry.timestamp)}
          </div>
          <div className="text-xs text-neutral-500 dark:text-neutral-400">
            {formatDate(entry.timestamp)}
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Summary card for entity audit history
 */
const AuditSummaryCard: React.FC<{ summary: AuditHistorySummary }> = ({ summary }) => {
  // THUB-186: subscribe to the timezone context so this component RE-RENDERS when the
  // selector changes. The formatter shims above read a module cache, which returns the
  // right value but cannot itself trigger a render -- without this the timestamps kept
  // showing the previous zone until a full page reload.
  useTimezone();
  return (
    <div className="bg-neutral-50 dark:bg-neutral-850 rounded-lg p-4 mb-4">
      <h3 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 mb-3">
        Change Summary
      </h3>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div>
          <div className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
            {summary.totalChanges}
          </div>
          <div className="text-xs text-neutral-500 dark:text-neutral-400">Total Changes</div>
        </div>
        <div>
          <div className="text-2xl font-bold text-success-600 dark:text-success-400">
            {summary.actionCounts.create}
          </div>
          <div className="text-xs text-neutral-500 dark:text-neutral-400">Creates</div>
        </div>
        <div>
          <div className="text-2xl font-bold text-primary-600 dark:text-primary-400">
            {summary.actionCounts.update}
          </div>
          <div className="text-xs text-neutral-500 dark:text-neutral-400">Updates</div>
        </div>
        <div>
          <div className="text-2xl font-bold text-danger-600 dark:text-danger-400">
            {summary.actionCounts.delete}
          </div>
          <div className="text-xs text-neutral-500 dark:text-neutral-400">Deletes</div>
        </div>
      </div>
      <div className="mt-3 pt-3 border-t border-neutral-200 dark:border-neutral-700">
        <div className="flex items-center justify-between text-xs">
          <span className="text-neutral-500 dark:text-neutral-400">
            Modified by: {summary.uniqueUsers.length} user{summary.uniqueUsers.length !== 1 ? 's' : ''}
          </span>
          <span className={`font-medium ${
            summary.currentState === 'deleted'
              ? 'text-danger-600 dark:text-danger-400'
              : 'text-success-600 dark:text-success-400'
          }`}>
            Status: {summary.currentState}
          </span>
        </div>
        {summary.createdAt && (
          <div className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
            Created: {formatDate(summary.createdAt)}
          </div>
        )}
        <div className="text-xs text-neutral-500 dark:text-neutral-400">
          Last modified: {formatDate(summary.lastModifiedAt)}
        </div>
      </div>
    </div>
  );
};

/**
 * Loading skeleton for audit log entries
 */
const AuditLogSkeleton: React.FC = () => (
  <div className="animate-pulse">
    {[1, 2, 3].map((i) => (
      <div key={i} className="p-4 border-b border-neutral-200 dark:border-neutral-700">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="h-5 bg-neutral-200 dark:bg-neutral-700 rounded w-24" />
            <div className="h-4 bg-neutral-200 dark:bg-neutral-700 rounded w-48 mt-2" />
          </div>
          <div className="text-right">
            <div className="h-4 bg-neutral-200 dark:bg-neutral-700 rounded w-16" />
            <div className="h-3 bg-neutral-200 dark:bg-neutral-700 rounded w-24 mt-1" />
          </div>
        </div>
      </div>
    ))}
  </div>
);

/**
 * Metadata-only fields that should not count as meaningful changes.
 * Audit entries where the ONLY changed fields are in this set are hidden
 * to reduce noise in the change history.
 */
const METADATA_ONLY_FIELDS = new Set([
  'updatedAt',
  'updatedBy',
  'version',
]);

/**
 * Returns true if an audit entry represents a meaningful change worth displaying.
 * Filters out:
 * - Update entries where the only changed fields are metadata (updatedAt, version, updatedBy)
 * - Update entries where changeDetails is empty or null
 * - Update entries where old and new values are identical for every changed field
 */
function isSignificantAuditEntry(entry: AuditLogEntry): boolean {
  // Non-update actions (create, delete) are always significant
  if (entry.action !== 'update') return true;

  // If there are no changeDetails at all, hide the entry
  if (!entry.changeDetails || entry.changeDetails.length === 0) return false;

  // Filter out metadata-only fields to see if any real fields changed
  const meaningfulFields = entry.changeDetails.filter(
    (field) => !METADATA_ONLY_FIELDS.has(field)
  );

  // If only metadata fields changed, hide the entry
  if (meaningfulFields.length === 0) return false;

  // If we have previousValue and newValue, check whether the meaningful fields
  // actually differ. If all meaningful fields have identical before/after values,
  // the entry is noise.
  if (entry.previousValue && entry.newValue) {
    const hasRealChange = meaningfulFields.some((field) => {
      const prev = JSON.stringify(entry.previousValue?.[field] ?? null);
      const next = JSON.stringify(entry.newValue?.[field] ?? null);
      return prev !== next;
    });
    if (!hasRealChange) return false;
  }

  return true;
}

/**
 * Empty state component
 */
const EmptyState: React.FC<{ message: string }> = ({ message }) => (
  <div className="p-8 text-center">
    <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center">
      <svg className="w-6 h-6 text-neutral-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    </div>
    <p className="text-neutral-500 dark:text-neutral-400">{message}</p>
  </div>
);

/**
 * AuditLogViewer Component
 *
 * Shows audit history for a specific entity or global search view
 */
export const AuditLogViewer: React.FC<AuditLogViewerProps> = (props) => {
  // Filter state for global view
  const [filterAction, setFilterAction] = useState<AuditAction | ''>('');
  const [filterEntityType, setFilterEntityType] = useState<AuditEntityType | ''>('');

  const isEntity = isEntityView(props);
  const entityType = isEntity ? props.entityType : undefined;
  const entityId = isEntity ? props.entityId : undefined;

  // THUB-299: this was six useState values plus a fetchAuditLogs useCallback and an effect
  // carrying an exhaustive-deps disable. That disable was not cosmetic -- `nextToken` was a
  // dependency of the callback, so the callback's identity changed on every page load, and only
  // the disable stopped the effect from re-running and resetting the list the user had just
  // paged through. The two modes are two different reads, so they are now two queries.
  const entityQuery = useQuery({
    queryKey: queryKeys.auditLog.entity(entityType ?? '', entityId ?? ''),
    queryFn: () =>
      auditLogApi.getEntityAuditHistory(entityType as AuditEntityType, entityId as string),
    enabled: isEntity,
  });

  // The filters are part of the key. Changing a filter starts a NEW pagination sequence, so
  // carrying the previous sequence's nextToken forward -- which the old code had to prevent by
  // hand -- is now structurally impossible.
  const searchQuery = useInfiniteQuery({
    queryKey: queryKeys.auditLog.search(filterAction, filterEntityType),
    queryFn: ({ pageParam }) => {
      const params: AuditLogSearchParams = {
        limit: 50,
        ...(filterAction && { action: filterAction }),
        ...(filterEntityType && { entityType: filterEntityType }),
        ...(pageParam ? { nextToken: pageParam as string } : {}),
      };
      return auditLogApi.searchAuditLogs(params);
    },
    initialPageParam: undefined as string | undefined,
    // A null nextToken means end of results; undefined is how TanStack says "no further pages",
    // which is what drives hasNextPage and therefore the Load More button.
    getNextPageParam: (last) => last.nextToken ?? undefined,
    enabled: !isEntity,
  });

  const activeQuery = isEntity ? entityQuery : searchQuery;
  const auditLogs: AuditLogEntry[] = isEntity
    ? entityQuery.data?.auditLogs ?? NO_LOGS
    : searchQuery.data?.pages.flatMap((page) => page.auditLogs) ?? NO_LOGS;
  const summary: AuditHistorySummary | null = entityQuery.data?.summary ?? null;
  const isLoading = activeQuery.isPending;
  const isLoadingMore = searchQuery.isFetchingNextPage;
  // Preserve the original message contract exactly: an Error's own message, else the default.
  const error =
    activeQuery.error instanceof Error
      ? activeQuery.error.message
      : activeQuery.error
        ? 'Failed to load audit logs'
        : null;

  // Filter controls for global view
  const renderFilters = () => {
    if (isEntity) return null;

    return (
      <div className="p-4 border-b border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-850">
        <div className="flex flex-wrap gap-3">
          <select
            value={filterEntityType}
            onChange={(e) => setFilterEntityType(e.target.value as AuditEntityType | '')}
            className="
              px-3 py-2 text-sm
              bg-white dark:bg-neutral-800
              border border-neutral-300 dark:border-neutral-600
              rounded-md
              text-neutral-900 dark:text-neutral-100
              focus:outline-none focus:ring-2 focus:ring-primary-500
            "
          >
            <option value="">All Entity Types</option>
            <option value="partner">Partners</option>
            <option value="connection">Connections</option>
            <option value="task">Tasks</option>
          </select>

          <select
            value={filterAction}
            onChange={(e) => setFilterAction(e.target.value as AuditAction | '')}
            className="
              px-3 py-2 text-sm
              bg-white dark:bg-neutral-800
              border border-neutral-300 dark:border-neutral-600
              rounded-md
              text-neutral-900 dark:text-neutral-100
              focus:outline-none focus:ring-2 focus:ring-primary-500
            "
          >
            <option value="">All Actions</option>
            <option value="create">Create</option>
            <option value="update">Update</option>
            <option value="delete">Delete</option>
          </select>
        </div>
      </div>
    );
  };

  // Render content
  const renderContent = () => {
    if (isLoading) {
      return <AuditLogSkeleton />;
    }

    if (error) {
      return (
        <div className="p-4 bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800 rounded-lg m-4">
          <p className="text-danger-700 dark:text-danger-300">{error}</p>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => activeQuery.refetch()}
            className="mt-2"
          >
            Retry
          </Button>
        </div>
      );
    }

    if (auditLogs.length === 0) {
      return <EmptyState message="No audit history found" />;
    }

    // Filter out noise: metadata-only updates and entries with no real changes
    const significantLogs = auditLogs.filter(isSignificantAuditEntry);

    if (significantLogs.length === 0) {
      return (
        <>
          {summary && <AuditSummaryCard summary={summary} />}
          <EmptyState message="No significant changes found (metadata-only updates were filtered out)" />
        </>
      );
    }

    return (
      <>
        {summary && <AuditSummaryCard summary={summary} />}

        <div className="border border-neutral-200 dark:border-neutral-700 rounded-lg overflow-hidden">
          {significantLogs.map((entry) => (
            <AuditLogRow
              key={entry.auditId}
              entry={entry}
              showEntityInfo={!isEntity}
            />
          ))}
        </div>

        {/* Load more button for global view */}
        {!isEntity && searchQuery.hasNextPage && (
          <div className="mt-4 text-center">
            <Button
              variant="secondary"
              onClick={() => searchQuery.fetchNextPage()}
              isLoading={isLoadingMore}
            >
              Load More
            </Button>
          </div>
        )}
      </>
    );
  };

  return (
    <div className="bg-white dark:bg-neutral-900 rounded-lg shadow-sm">
      {/* Header */}
      <div className="p-4 border-b border-neutral-200 dark:border-neutral-700">
        <h2 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
          {isEntity
            ? (props as EntityAuditViewerProps).title || 'Change History'
            : 'Audit Log'
          }
        </h2>
        {isEntity && (
          <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">
            Complete history of configuration changes
          </p>
        )}
      </div>

      {/* Filters */}
      {renderFilters()}

      {/* Content */}
      <div className="p-4">
        {renderContent()}
      </div>
    </div>
  );
};

export default AuditLogViewer;
